if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (this.form) this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        
        this.variantIdInput.disabled = false;

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.product = this.closest('[data-component="card-wrapper"]')
				if (this.product) this.colorVariants = this.product.querySelector('card-variants')

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.cartShouldStayEmpty = false;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        if (this.submitButton.querySelector('.loading__spinner')) this.submitButton.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        const quantityInput = evt.target.closest('.slide-visible')?.querySelector('[name="quantity"]');
        if (quantityInput) {
          formData.set('quantity', quantityInput.value);
        }

        if (this.cart) {
          const cartSections = this.cart.getSectionsToRender?.() || [];
          formData.append('sections', cartSections.map((section) => section.id));

          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);

        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {

            if (response.status) {

              const errors = response.errors;
              const isGiftCardRecipientError =
                errors &&
                typeof errors === 'object' &&
                Object.keys(errors).some((key) =>
                  ['email', 'name', 'message', 'send_on'].includes(key)
                );
            
              if (isGiftCardRecipientError) {
                this.cartShouldStayEmpty = true;
              }

              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              if (this.cart && !isGiftCardRecipientError) {
                this.refreshCartAfterAvailabilityError(response);
              }

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage && !isGiftCardRecipientError) {
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
            
                if (this.colorVariants) {
                  this.colorVariants.disableCurrentVariant();
                }
            
                this.error = true;
              }

              return;

            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const isSubscriptionResponse = !!response.sections && !response.id && !response.key;

            if (isSubscriptionResponse) {
              this.cartShouldStayEmpty = true;
              this.error = false;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });

            sessionStorage.setItem(`product-added-to-cart-${formData.get('product-id')}`, 'true');
            
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');

            if ( this.cart && this.cart.classList.contains('is-empty') && !this.error && !this.cartShouldStayEmpty) {
              this.cart.classList.remove('is-empty');
            }

            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            if (this.submitButton.querySelector('.loading__spinner')) this.submitButton.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async refreshCartAfterAvailabilityError(response) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) return;

        try {
          const cart = await fetch(`${routes.cart_url}.js`).then((res) => res.json());
          if (!cart.item_count) return;

          const sectionIds = ['cart-drawer', 'cart-icon-bubble'];
          const sections = {};

          await Promise.all(
            sectionIds.map(async (sectionId) => {
              const html = await fetch(`${routes.cart_url}?section_id=${encodeURIComponent(sectionId)}`).then((res) =>
                res.text()
              );
              sections[sectionId] = html;
            })
          );

          cartDrawer.classList.remove('is-empty');
          // Update cart silently — do not open drawer on inventory limit (Quick View stays open)
          cartDrawer.renderContents({ ...response, sections, item_count: cart.item_count }, false);
        } catch (error) {
          console.error(error);
        }
      }

      async updateBlocksOnError() {
        try {
          const response = await fetch(window.shopUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch: ${response.status}`);
          }
          
          const text = await response.text();
          const parser = new DOMParser();
          const newDocument = parser.parseFromString(text, 'text/html');

          this.getSectionsToRender().forEach((section) => {
            let newContent;
            let currentElement;
            
            if (section.id && section.selector) {
              const sectionElement = newDocument.getElementById(section.id);
              if (sectionElement) {
                newContent = sectionElement.querySelector(section.selector);
                currentElement = document.getElementById(section.id)?.querySelector(section.selector);
                if (newContent && currentElement) {
                  currentElement.innerHTML = newContent.innerHTML;
                }
              }
            } else if (section.id) {
              newContent = newDocument.getElementById(section.id);
              currentElement = document.getElementById(section.id);
              if (newContent && currentElement) {
                currentElement.innerHTML = newContent.innerHTML;
              }
            } else if (section.selector) {
              newContent = newDocument.querySelector(section.selector);
              currentElement = document.querySelector(section.selector);
              if (newContent && currentElement) {
                currentElement.innerHTML = newContent.innerHTML;
              }
            }
          });
        } catch (error) {
          console.error('Error updating blocks on error:', error);
        }
      }

      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            section: 'cart-drawer',
            selector: '.drawer__inner',
          },
          {
            id: 'cart-icon-bubble',
          }
        ];
      }

      handleErrorMessage(errorMessage = false) {

        if (this.hideErrors) return;
        
        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        
        if (!this.errorMessageWrapper) return;
        
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
        
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        
        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const isPreorder = this.dataset?.preorder === 'true' || this.closest('[data-preorder="true"]');
        const hasVariant = Boolean(this.variantIdInput?.value);

        if (isPreorder && hasVariant) {
          disable = false;
        }

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) {
            this.submitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent =
            isPreorder && window.variantStrings?.preOrder
              ? window.variantStrings.preOrder
              : window.variantStrings.addToCart;
        }
      }

      toggleDynamicCheckout(disable = false) {
        const isPreorder = this.dataset?.preorder === 'true' || this.closest('[data-preorder="true"]');
        this.toggleAttribute('data-preorder-soldout', Boolean(disable) && isPreorder);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}